import React, { useEffect, useState } from 'react'
import { PasswordInput } from '../../../../../../../shared/components/password-input/PasswordInput'
import { PasswordConfirmInput } from '../../../../../../../shared/components/password-confirm-input/PasswordConfirmInput'
import { ESignupFormSteps } from '../../../../../../../types/enums/ESignupFormSteps'
import { HiddenEmail, UserExistsError } from './style'
import { signUp } from '../../../../../../../services/AuthService'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../store/store'
import { updateUser } from '../../../../../../../store/reducers/userReducer'
import { ProgressBar } from 'shared/components/progress-bar/ProgressBar'
import { FormTypes } from 'types/enums/EFormTypes'
import { InputsWrapper } from '../../style'
import { setFormTranslatedText } from 'pages/auth/utils/setFormTranslatedText'
import PasswordInstructions from 'pages/auth/common/PasswordInstructions'
import { useTranslation } from 'react-i18next'
import { setForm, setIsLayoutLoading } from 'store/reducers/layoutReducer'
import { EmailInput } from 'shared/components/email-input/EmailInput'
import { useNavigate } from 'react-router-dom'
import { EAppRoutes } from 'types/enums/EAppRoutes'
import { Button } from 'shared/components/button/Button'

export const SignupFormPassword: React.FC = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const [signupFormPasswordData, setSignupFormPasswordData] = useState<{
    password: string
    passwordConfirm: string
  }>({ password: '', passwordConfirm: '' })
  const [errors, setErrors] = useState<{
    password?: boolean
    passwordConfirm?: boolean
  }>({
    password: false,
    passwordConfirm: false,
  })
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [showUserExistsError, setShowUserExistsError] = useState<boolean>(false)

  setFormTranslatedText('signUp.password')
  const { t } = useTranslation('', {
    keyPrefix: `pages.signUp.password`,
  })

  useEffect(() => {
    if (
      signupFormPasswordData.password &&
      signupFormPasswordData.passwordConfirm &&
      !errors.password &&
      !errors.passwordConfirm
    ) {
      setIsDisabled(false)
    } else if (!isDisabled) {
      setIsDisabled(true)
    }
  }, [signupFormPasswordData])

  const handlePasswordChange = (value: string, error?: boolean) => {
    setErrors({ ...errors, password: error })
    setSignupFormPasswordData({ ...signupFormPasswordData, password: value })
  }

  const handlePasswordConfirmChange = (value: string, error?: boolean) => {
    setErrors({ ...errors, passwordConfirm: error })
    setSignupFormPasswordData({
      ...signupFormPasswordData,
      passwordConfirm: value,
    })
  }

  const handlePasswordSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()
    // cognito
    const signUpParams = {
      username: user.email!,
      password: signupFormPasswordData.password,
    }

    const signedUser = await signUp(signUpParams)
    if (!signedUser || 'error' in signedUser) {
      setShowUserExistsError(true)
      dispatch(setIsLayoutLoading(false))
      return
    }

    setShowUserExistsError(false)
    dispatch(updateUser({ password: signupFormPasswordData.password }))
    dispatch(setForm({ formStep: ESignupFormSteps.confirmation }))
  }

  const navigate = useNavigate()
  const navigateToLogin = () => {
    dispatch(setForm({ formType: FormTypes.LOGIN_FORM }))
    navigate(EAppRoutes.LOGIN)
  }
  return (
    <>
      <PasswordInstructions />
      <InputsWrapper>
        <HiddenEmail>
          {/* for Password Manager Username */}
          <EmailInput value={user.email} callback={() => {}} />
        </HiddenEmail>
        <PasswordInput
          labelText={t('inputs.password.labelText')}
          errorText={t('inputs.password.errorText')}
          hasError={errors.password}
          callback={handlePasswordChange}
          type="new"
        ></PasswordInput>
        <PasswordConfirmInput
          hasError={errors.passwordConfirm}
          labelText={t('inputs.passwordConfirm.labelText')}
          errorText={t('inputs.passwordConfirm.errorText')}
          password={signupFormPasswordData.password}
          callback={handlePasswordConfirmChange}
        ></PasswordConfirmInput>
        <UserExistsError className={showUserExistsError ? 'error' : ''}>
          {t('buttons.next.errorText')}
        </UserExistsError>
      </InputsWrapper>
      <Button
        type="submit"
        disabled={isDisabled}
        className={`button ${isDisabled ? 'disabled' : ''}`}
        text={t('buttons.next.text')}
        callback={handlePasswordSubmit}
      />
      <Button
        type="reset"
        disabled={false}
        text={
          showUserExistsError ? t('buttons.back.text') : t('buttons.prev.text')
        }
        callback={
          showUserExistsError
            ? navigateToLogin
            : () =>
                dispatch(
                  setForm({ formStep: ESignupFormSteps.verificationOption }),
                )
        }
      />
      <ProgressBar progress={20} />
    </>
  )
}
